/* eslint-disable class-methods-use-this */
import { inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { filter } from 'rxjs';
import { DatadogConfig, DatadogContext } from './datadog.const';

@Injectable({
  providedIn: 'root',
})
export class DatadogService implements OnDestroy {
  private static started = false;

  readonly #router = inject(Router);

  readonly #activatedRoute = inject(ActivatedRoute);

  static init(config: DatadogConfig) {
    if (!DatadogService.started) {
      datadogRum.init({
        ...config,
        site: config.site ?? 'datadoghq.eu',
        sessionSampleRate: config.sessionSampleRate ?? 25,
        sessionReplaySampleRate: config.sessionReplaySampleRate ?? 10,
        defaultPrivacyLevel: config.defaultPrivacyLevel ?? 'mask-user-input',
      });

      DatadogService.started = true;
    }
  }

  constructor() {
    this.#router.events
      .pipe(
        takeUntilDestroyed(),
        filter((e) => e instanceof NavigationEnd),
      )
      .subscribe(() => this.#setCurrentView());
  }

  get started() {
    return DatadogService.started;
  }

  ngOnDestroy(): void {
    if (DatadogService.started) {
      datadogRum.stopSession();
      datadogRum.stopSessionReplayRecording();
      datadogRum.clearGlobalContext();
      datadogRum.clearUser();
      datadogRum.clearAccount();
      DatadogService.started = false;
    }
  }

  setUser(user: {
    [DatadogContext.UserReference]: string;
    [DatadogContext.Cpis]?: string | Array<string>;
    [DatadogContext.BrinCode]?: string;
    [DatadogContext.Role]?: string;
  }) {
    datadogRum.setUser(user);
  }

  addViewContext(key: string, value: unknown) {
    datadogRum.setViewContextProperty(key, value);
  }

  addFeatureFlag(feature: string, value: boolean) {
    datadogRum.addFeatureFlagEvaluation(feature.replaceAll('.', '_'), value);
  }

  #setCurrentView() {
    let currentRoute = this.#activatedRoute.root;

    if (!currentRoute) {
      return;
    }

    const features: Array<string> = [];

    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;

      const { data } =
        currentRoute.snapshot.routeConfig ?? currentRoute.snapshot;
      const featureName = data?.[DatadogContext.Feature];

      if (featureName) {
        features.push(featureName);
      }
    }

    const context = {
      ...datadogRum.getGlobalContext(),
      ...datadogRum.getInitConfiguration(),
    };

    datadogRum.startView({
      service: context.service,
      version: context.version,
      name: features.length > 0 ? features.join(' > ') : 'unknown',
    });
  }
}
